import { Icon } from '@inflearn/ds-react-next';
import {
  faBooks,
  faCircleUser,
  faFolderHeart,
  faGear,
  faHandshake,
  faPenToSquare,
  faReceipt,
  faTableLayout,
} from '@inflearn/pro-regular-svg-icons';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { MY_URL, USERS_URL } from '../../../../constants/urls';
import useClientAccountProfileQuery from '../../../../queries/useClientAccountProfileQuery';

export function useSidebarLinks(): Array<{
  label: string;
  icon: JSX.Element;
  href: string;
  isActive: (currentPath: string) => boolean;
  isExternal: boolean;
}> {
  const { data: user } = useClientAccountProfileQuery();
  const userId = String(user?.id);
  const { t } = useTranslation();
  const isPartner = !!user?.marketingPartner;

  return useMemo(
    () => [
      {
        label: t('프로필'),
        icon: <Icon icon={faCircleUser} size="lg" fixedWidth />,
        href: USERS_URL.HOME({ userId }),
        isExternal: false,
        isActive: (currentPath) => {
          const currentPathname = currentPath.split('?')[0];
          const profilePathname = new URL(USERS_URL.HOME({ userId })).pathname;
          const dashboardPathname = new URL(USERS_URL.DASHBOARD({ userId })).pathname;

          // 대시보드가 아닐 때만 프로필 활성화
          return (
            currentPathname.startsWith(profilePathname) &&
            !currentPathname.startsWith(dashboardPathname)
          );
        },
      },
      {
        label: t('대시보드'),
        icon: <Icon icon={faTableLayout} size="lg" fixedWidth />,
        href: USERS_URL.DASHBOARD({ userId }),
        isExternal: false,
        isActive: (currentPath) => defaultIsActive(currentPath, USERS_URL.DASHBOARD({ userId })),
      },
      {
        label: t('내 학습'),
        icon: <Icon icon={faBooks} size="lg" fixedWidth />,
        href: MY_URL.COURSES(),
        isExternal: false,
        isActive: (currentPath) =>
          matchAnyPath(currentPath, [
            MY_URL.COURSES(),
            MY_URL.MENTORINGS(),
            MY_URL.CLIPS(),
            MY_URL.NOTES(),
            MY_URL.VOUCHERS(),
            MY_URL.CERTIFICATES(),
          ]),
      },
      {
        label: t('구매 / 혜택'),
        icon: <Icon icon={faReceipt} size="lg" fixedWidth />,
        href: MY_URL.ORDERS(),
        isExternal: false,
        isActive: (currentPath) => {
          return matchAnyPath(currentPath, [
            MY_URL.ORDERS(),
            MY_URL.GIFTS_RECEIVED(),
            MY_URL.GIFTS_SENT(),
          ]);
        },
      },
      {
        label: t('보관함'),
        icon: <Icon icon={faFolderHeart} size="lg" fixedWidth />,
        href: MY_URL.LIKES(),
        isExternal: false,
        isActive: (currentPath) => {
          return matchAnyPath(currentPath, [MY_URL.LIKES(), MY_URL.LIST()]);
        },
      },
      {
        label: t('작성한 게시글'),
        icon: <Icon icon={faPenToSquare} size="lg" fixedWidth />,
        href: MY_URL.QUESTION(),
        isExternal: false,
        isActive: (currentPath) => {
          const currentPathname = currentPath.split('?')[0];
          // /my/posts로 시작하는 모든 경로에서 활성화
          return currentPathname.startsWith('/my/posts');
        },
      },
      ...(isPartner
        ? [
            {
              label: t('파트너스'),
              icon: <Icon icon={faHandshake} size="lg" fixedWidth />,
              href: MY_URL.PARTNERS(),
              isExternal: false,
              isActive: (currentPath: string) => defaultIsActive(currentPath, MY_URL.PARTNERS()),
            },
          ]
        : []),
      {
        label: t('계정 설정'),
        icon: <Icon icon={faGear} size="lg" fixedWidth />,
        href: MY_URL.ACCOUNT(),
        isExternal: false,
        isActive: (currentPath) => {
          const currentPathname = currentPath.split('?')[0];
          // /my/settings로 시작하는 모든 경로에서 활성화
          return currentPathname.startsWith('/my/settings');
        },
      },
    ],
    [userId, isPartner],
  );
}

/**
 * 기본 활성화 매칭 로직 - 현재 경로가 링크 경로로 시작하는지 확인
 */
function defaultIsActive(currentPath: string, linkPath: string): boolean {
  const currentPathname = currentPath.split('?')[0];
  const linkPathname = new URL(linkPath).pathname;
  return currentPathname.startsWith(linkPathname);
}

/**
 * 여러 경로 중 하나라도 일치하면 활성화
 */
function matchAnyPath(currentPath: string, urls: string[]): boolean {
  const currentPathname = currentPath.split('?')[0];
  const targetPathnames = urls.map((url) => new URL(url).pathname);
  return targetPathnames.includes(currentPathname);
}
